import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Collapse,
} from '@mui/material';
import { Close as CloseIcon, Minimize as MinimizeIcon, CropSquare as MaximizeIcon } from '@mui/icons-material';

interface MiniVideoPlayerProps {
  videoUrl: string;
  title: string;
  onClose: () => void;
}

const MiniVideoPlayer = ({ videoUrl, title, onClose }: MiniVideoPlayerProps) => {
  const [isMinimized, setIsMinimized] = useState(false);

  const playerSize = { width: 400, height: 280 };

  const getVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const videoId = getVideoId(videoUrl);

  if (!videoId) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1300,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#000',
        width: playerSize.width,
        height: isMinimized ? 40 : playerSize.height,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, mr: 1 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(!isMinimized);
            }}
            sx={{ 
              color: 'white', 
              p: 0.5,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
            title={isMinimized ? 'Expand video' : 'Minimize video'}
          >
            {isMinimized ? (
              <MaximizeIcon sx={{ fontSize: '14px' }} />
            ) : (
              <MinimizeIcon sx={{ fontSize: '16px' }} />
            )}
          </Box>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{ 
              color: 'white', 
              p: 0.5,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
            title="Close video player"
          >
            <CloseIcon sx={{ fontSize: '16px' }} />
          </Box>
        </Box>
      </Box>

      <Collapse in={!isMinimized}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: playerSize.height - 40,
            backgroundColor: '#000',
          }}
        >
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=1`}
            title={title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
      </Collapse>

      {isMinimized && (
        <Box
          sx={{
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            textAlign: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setIsMinimized(false)}
        >
          <Typography variant="body2">
            Click to expand video
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MiniVideoPlayer;